/* eslint-disable react/sort-comp, camelcase, one-var, prefer-template, jsx-a11y/no-static-element-interactions */
import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { fromJS } from "immutable";
import { getWorkspaceItemSource } from "../../helper/URLHelper";
import { getMouseClientPosition } from "../../helper/TransformManagerHelper";
import { isVideoOnly } from "../timeline/timeline-helper";
import { getAiReframeStaticX } from "../../helper/reframeHelper";
import { CropSelectionBox } from "./tm-components";
import { cropImage, applyFrameClipPlot } from "../../redux/actions/timelineUtils";
import { toggleCrop } from "../../redux/actions/appUtils";
import { STATIC_PATH } from "../../constants/config";
import { ItemToolConfirmation } from "../itemtoolbar/itemtoolbar-confirmation";

// ─────────────────────────────────────────────────────────────────────────────
// Memoization cache for video frame captures
// ─────────────────────────────────────────────────────────────────────────────
const frameCache = new Map();
const MAX_FRAME_CACHE_SIZE = 10;

const getCacheKey = (src, timeInSeconds) => `${src}_${Math.round(timeInSeconds * 10) / 10}`;

const evictOldestCacheEntry = () => {
    if (frameCache.size >= MAX_FRAME_CACHE_SIZE) {
        const firstKey = frameCache.keys().next().value;
        frameCache.delete(firstKey);
    }
};
/**
 * Captures a frame from a video source at a specific time and returns a data URL.
 */
const captureVideoFrameAtTime = (src, timeInSeconds) => {
    const cacheKey = getCacheKey(src, timeInSeconds);
    
    // Check cache first
    if (frameCache.has(cacheKey)) {
        return Promise.resolve(frameCache.get(cacheKey));
    }

    return new Promise((resolve) => {
        try {
            const video = document.createElement("video");
            video.crossOrigin = "anonymous";
            video.muted = true;
            video.preload = "metadata"; // OPTIMIZATION: Only preload metadata initially
            video.src = src;
            
            const cleanup = () => {
                video.onloadedmetadata = null;
                video.onseeked = null;
                video.onerror = null;
                video.src = ""; // Release video resource
            };

            video.onloadedmetadata = () => {
                video.currentTime = timeInSeconds || 0;
            };
            video.onseeked = () => {
                try {
                    const canvas = document.createElement("canvas");
                    //  Use smaller canvas for thumbnail if video is large
                    const maxDim = 1280;
                    const scale = Math.min(1, maxDim / Math.max(video.videoWidth || 640, video.videoHeight || 360));
                    canvas.width = (video.videoWidth || 640) * scale;
                    canvas.height = (video.videoHeight || 360) * scale;
                    const ctx = canvas.getContext("2d", { alpha: false }); // OPTIMIZATION: Disable alpha for better performance
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
                    
                    // Cache the result
                    evictOldestCacheEntry();
                    frameCache.set(cacheKey, dataUrl);
                    
                    cleanup();
                    resolve(dataUrl);
                } catch (err) {
                    cleanup();
                    resolve(null);
                }
            };
            video.onerror = () => {
                cleanup();
                resolve(null);
            };
        } catch (err) {
            resolve(null);
        }
    });
};

class CropComponent extends React.Component {
    constructor(props) {
        super(props);

        this.getInitialState = this.getInitialState.bind(this);
        this.getWorkspaceBounds = this.getWorkspaceBounds.bind(this);

        this.initiateMove = this.initiateMove.bind(this);
        this.moveSelection = this.moveSelection.bind(this);
        this.stopMove = this.stopMove.bind(this);

        this.initiateCropResize = this.initiateCropResize.bind(this);
        this.resizeCropSelection = this.resizeCropSelection.bind(this);
        this.stopCropResize = this.stopCropResize.bind(this);

        this.initiateResize = this.initiateResize.bind(this);
        this.resizeSelection = this.resizeSelection.bind(this);
        this.stopResize = this.stopResize.bind(this);

        this.getUnCroppedXY = this.getUnCroppedXY.bind(this);
        this.getMousePosition = this.getMousePosition.bind(this);
        this.getUnCroppedResizePosition = this.getUnCroppedResizePosition.bind(this);
        this.resetCrop = this.resetCrop.bind(this);

        this.handleCropClick = this.handleCropClick.bind(this);
        this.cancelCrop = this.cancelCrop.bind(this);
        this.handleKeyboardShortcuts = this.handleKeyboardShortcuts.bind(this);

        this.mouseStartPosition = { x: null, y: null, offsetX: null, offsetY: null };
        this.lastDown = null;
        this.moved = false;
        this.xLocked = false;
        this.lockedX = null;
        this.yLocked = false;
        this.lockedY = null;
        
        // RAF tracking for smooth animations
        this.animationFrameId = null;
        
        // Cached computed values to avoid recalculation during drag
        this._cachedFlipPosition = null;

        this.state = this.getInitialState();
    }

    getRotatedPoint = (px, py, x, y, angle) => {
        const angle_in_rad = angle * Math.PI / 180;
        return {
            x: (x - px) * Math.cos(angle_in_rad) - (y - py) * Math.sin(angle_in_rad) + px,
            y: (x - px) * Math.sin(angle_in_rad) + (y - py) * Math.cos(angle_in_rad) + py,
        }
    }

    getInitialState() {
        const stateToUpdate = {};
        const { workspaceWidth, workspaceHeight, cropItem } = this.props;

        let cropX, cropY, cropWidth, cropHeight;
        let selectionX, selectionY, selectionWidth, selectionHeight;

        if (cropItem) {
            const isSingleClipFrame = cropItem.get("isSingleClipFrame");
            const firstClipId = isSingleClipFrame ? cropItem.get("clipDetails")?.keySeq().first() : null;
            const targetItem = firstClipId ? cropItem.getIn(["clipDetails", firstClipId, "imgDetails"]) : cropItem;

            if (targetItem && targetItem.get("original") && (targetItem.get("isCropped") || isSingleClipFrame)) {
                const pwidth = targetItem.get("pwidth") || cropItem.get("pwidth");
                const pheight = targetItem.get("pheight") || cropItem.get("pheight");
                const videoAspectRatio = pwidth / pheight;
            
                const itemWidth = cropItem.get("width");
                const itemHeight = cropItem.get("height");
                
                selectionHeight = itemHeight * targetItem.getIn(["original", "height"]);
                selectionWidth = selectionHeight * videoAspectRatio;
                
                const originalXRatio = targetItem.getIn(["original", "x"]);
                const originalYRatio = targetItem.getIn(["original", "y"]);
                
                selectionX = cropItem.get("x") + originalXRatio * itemWidth;
                selectionY = cropItem.get("y") + originalYRatio * itemHeight;

                cropWidth = itemWidth;
                cropHeight = itemHeight;
                cropX = cropItem.get("x") - selectionX;
                cropY = cropItem.get("y") - selectionY;

            } else {
                selectionX = cropItem.get("x");
                selectionY = cropItem.get("y");
                selectionWidth = cropItem.get("width");
                selectionHeight = cropItem.get("height");

                cropWidth = cropItem.get("width") * 0.75;
                cropHeight = cropItem.get("height") * 0.75;

                cropX = ((cropItem.get("width") / 2) - (cropWidth / 2));
                cropY = ((cropItem.get("height") / 2) - (cropHeight / 2));
            }

            
            // Additional debug for understanding the issue
           
            let dX = 0, dY = 0;
            if (cropItem.get("flipPosition") === 1) {
                dX = (cropX - (selectionWidth - (cropX + cropWidth)));
            } else if (cropItem.get("flipPosition") === 2) {
                dY = (cropY - (selectionHeight - (cropY + cropHeight)));
            } else if (cropItem.get("flipPosition") === 3) {
                dX = (cropX - (selectionWidth - (cropX + cropWidth)));
                dY = (cropY - (selectionHeight - (cropY + cropHeight)));
            }

            const unCroppedX = dX;
            const unCroppedY = dY;
            const cropImageX = (-1 * cropX) + dX;
            const cropImageY = (-1 * cropY) + dY;
            const { src, thumb } = getWorkspaceItemSource({
                item: {
                    bgremoval: cropItem.get("bgremoval"),
                    isBlob: cropItem.get("isBlob"),
                    src: cropItem.get("src"),
                    stickerify: cropItem.get("stickerify"),
                    subType: cropItem.get("subType"),
                    thumb: cropItem.get("thumb"),
                    thumbnail: cropItem.get("thumbnail"),
                    type: cropItem.get("type"),
                },
                workspaceWidth,
                workspaceHeight,
            });
            let imgSrc = src;
            if (isVideoOnly(cropItem.get("type"), cropItem.get("subType"))) {
                imgSrc = thumb;
            }

            const angle = cropItem.get("angle");
            stateToUpdate.type = cropItem.get("type");
            stateToUpdate.cropSelectionBox = { x: selectionX, y: selectionY, width: selectionWidth, height: selectionHeight, angle };
            stateToUpdate.cropBox = { x: cropX, y: cropY, width: cropWidth, height: cropHeight };
            stateToUpdate.cropImage = { x: cropImageX, y: cropImageY, width: selectionWidth, height: selectionHeight };
            stateToUpdate.unCroppedBox = { x: unCroppedX, y: unCroppedY, width: selectionWidth, height: selectionHeight };
            stateToUpdate.image = imgSrc;
        }

        return stateToUpdate;
    }

    componentDidMount() {
        const { cropItem, playhead } = this.props;
        if (cropItem && cropItem.get("sceneData") && isVideoOnly(cropItem.get("type"), cropItem.get("subType"))) {
            const { src } = getWorkspaceItemSource({
                item: {
                    bgremoval: cropItem.get("bgremoval"),
                    isBlob: cropItem.get("isBlob"),
                    src: cropItem.get("src"),
                    stickerify: cropItem.get("stickerify"),
                    subType: cropItem.get("subType"),
                    thumb: cropItem.get("thumb"),
                    thumbnail: cropItem.get("thumbnail"),
                    type: cropItem.get("type"),
                },
                workspaceWidth: this.props.workspaceWidth,
                workspaceHeight: this.props.workspaceHeight,
            });
            // Compute exact video time = videoStart + how far into the scene the playhead is
            const videoStart = cropItem.get("videoStart") || 0;
            const enterStart = cropItem.get("enterStart") || 0;
            const exactVideoTime = videoStart + Math.max(0, (playhead || 0) - enterStart);
            captureVideoFrameAtTime(src, exactVideoTime).then((frameDataUrl) => {
                if (frameDataUrl) {
                    this.setState({ image: frameDataUrl });
                }
            });

            const panOffset = (this.props.aiReframeX || 0) / this.props.zoomFactor;
            if (panOffset !== 0) {
                // Store so handleCropClick can restore static position when saving
                this.aiReframePanOffset = panOffset;
                this.setState((prevState) => ({
                    cropBox: { ...prevState.cropBox, x: prevState.cropBox.x - panOffset },
                    // Shift the image inside cropBox so it shows the correct video portion
                    cropImage: { ...prevState.cropImage, x: prevState.cropImage.x + panOffset },
                    // Also store panOffset so the background (unCropped) image can show the same region
                    unCroppedImagePanOffset: panOffset,
                }));
            }
        }
    }

    getWorkspaceBounds() {
        return this.props.workspaceRef.current.getBoundingClientRect();
    }

    getMousePosition(e) {
        let pos = { x: 0, y: 0 };
        try {
            const workspaceBounds = this.getWorkspaceBounds();
            const unrotatedPostion = getMouseClientPosition(e, fromJS({ x: -workspaceBounds.x, y: -workspaceBounds.y }));

            /* handle mouse position for rotated item
             * e.g. when image is rotated to 90deg and cropped horizontal movement should actually be vertical movement
             */
            const { angle } = this.state.cropSelectionBox;
            const px = workspaceBounds.width / 2;
            const py = workspaceBounds.height / 2;
            pos = this.getRotatedPoint(px, py, unrotatedPostion.get("x"), unrotatedPostion.get("y"), -angle);
        } catch (error) { }
        return pos;
    }

    initiateMove(e) {
        try {
            e.preventDefault();
            e.stopPropagation();
            this.lastDown = e.timeStamp;
            this.moved = false;
            const mouse_position = this.getMousePosition(e);
            this.mouseStartPosition = Object.assign(this.mouseStartPosition, { x: mouse_position.x, y: mouse_position.y });
            const unCroppedBox = { ...this.state.unCroppedBox };
            this.setState({
                unCroppedBox: Object.assign(unCroppedBox, { transforming: true })
            });
            window.addEventListener('mousemove', this.moveSelection, false);
            window.addEventListener('mouseup', this.stopMove, false);
            window.addEventListener('touchmove', this.moveSelection, false);
            window.addEventListener('touchend', this.stopMove, false);
        } catch (error) { }
    }

    getUnCroppedXY(e) {
        const mouse_position = this.getMousePosition(e);
        let x = this.state.unCroppedBox.x - ((this.mouseStartPosition.x - mouse_position.x) / this.props.zoomFactor);
        let y = this.state.unCroppedBox.y - ((this.mouseStartPosition.y - mouse_position.y) / this.props.zoomFactor);

        if (x >= this.state.cropBox.x)
            x = this.state.cropBox.x;
        if (y >= this.state.cropBox.y)
            y = this.state.cropBox.y;
        if ((x + this.state.unCroppedBox.width) <= (this.state.cropBox.x + this.state.cropBox.width))
            x = (this.state.cropBox.x + this.state.cropBox.width) - this.state.unCroppedBox.width;
        if ((y + this.state.unCroppedBox.height) <= (this.state.cropBox.y + this.state.cropBox.height))
            y = (this.state.cropBox.y + this.state.cropBox.height) - this.state.unCroppedBox.height;

        if (x >= this.state.cropBox.x + this.state.cropBox.width) {
            x = this.state.cropBox.x + this.state.cropBox.width;
        }
        if ((x + this.state.unCroppedBox.width) <= this.state.cropBox.x) {
            x = this.state.cropBox.x - this.state.unCroppedBox.width;
        }
        if (y >= this.state.cropBox.y + this.state.cropBox.height) {
            y = this.state.cropBox.y + this.state.cropBox.height;
        }
        if ((y + this.state.unCroppedBox.height) <= this.state.cropBox.y) {
            y = this.state.cropBox.y - this.state.unCroppedBox.height;
        }

        return { x, y };
    }

    moveSelection(e) {
        try {
            if (this.lastDown + 10 < e.timeStamp) {
                if (!this.moved)
                    this.moved = true;
                e.preventDefault();
                
                // Use requestAnimationFrame for smooth updates
                const XY = this.getUnCroppedXY(e);
                
                if (this.animationFrameId) {
                    cancelAnimationFrame(this.animationFrameId);
                }
                
                this.animationFrameId = requestAnimationFrame(() => {
                    if (!this.unCroppedBoxElement || !this.cropImageElem) return;
                    
                    const { zoomFactor } = this.props;
                    const flip = this.getFlipPosition();
                    const angle = this.getAngle();
                    const cropImageX = (this.state.cropImage.x + (XY.x - this.state.unCroppedBox.x)) * zoomFactor;
                    const cropImageY = (this.state.cropImage.y + (XY.y - this.state.unCroppedBox.y)) * zoomFactor;
                    
                    this.unCroppedBoxElement.style.transform = `translate(${XY.x * zoomFactor}px, ${XY.y * zoomFactor}px)`;
                    this.cropImageElem.style.transform = `translate(${cropImageX}px, ${cropImageY}px) rotate(${angle}deg)${flip}`;
                });
            }
        } catch (error) { }
    }

    stopMove(e) {
        try {
            e.preventDefault();
            e.stopPropagation();

            let { unCroppedBox, cropImage } = this.state;

            unCroppedBox = { ...unCroppedBox };
            cropImage = { ...cropImage };

            if (this.moved) {
                const XY = this.getUnCroppedXY(e);
                cropImage = Object.assign(
                    cropImage,
                    {
                        x: (cropImage.x + (XY.x - unCroppedBox.x)),
                        y: (cropImage.y + (XY.y - unCroppedBox.y))
                    }
                );
                unCroppedBox = Object.assign(unCroppedBox, { x: XY.x, y: XY.y });
            }
            unCroppedBox.transforming = false;

            this.setState({ unCroppedBox, cropImage });
        } catch (error) { }

        window.removeEventListener('mousemove', this.moveSelection, false);
        window.removeEventListener('mouseup', this.stopMove, false);
        window.removeEventListener('touchmove', this.moveSelection, false);
        window.removeEventListener('touchend', this.stopMove, false);
    }

    handleKeyboardShortcuts(option) {
        if (this.state.unCroppedBox !== null) {
            let { x, y } = this.state.unCroppedBox;

            const d = 1, sd = 10;
            switch (option) {
                case "RIGHT_ARROW":
                case "RIGHT_ARROW_AGAIN":
                    x += d;
                    y = this.state.unCroppedBox.y;
                    break;
                case "LEFT_ARROW":
                case "LEFT_ARROW_AGAIN":
                    x -= d;
                    y = this.state.unCroppedBox.y;

                    break;
                case "UP_ARROW":
                case "UP_ARROW_AGAIN":
                    y -= d;
                    x = this.state.unCroppedBox.x;

                    break;
                case "DOWN_ARROW":
                case "DOWN_ARROW_AGAIN":
                    y += d;
                    x = this.state.unCroppedBox.x;

                    break;
                case "SHIFT_RIGHT_ARROW":
                case "SHIFT_RIGHT_ARROW_AGAIN":
                    x += sd;
                    y = this.state.unCroppedBox.y;

                    break;
                case "SHIFT_LEFT_ARROW":
                case "SHIFT_LEFT_ARROW_AGAIN":
                    x -= sd;
                    y = this.state.unCroppedBox.y;
                    break;
                case "SHIFT_UP_ARROW":
                case "SHIFT_UP_ARROW_AGAIN":
                    y -= sd;
                    x = this.state.unCroppedBox.x;

                    break;
                case "SHIFT_DOWN_ARROW":
                case "SHIFT_DOWN_ARROW_AGAIN":
                    y += sd;
                    x = this.state.unCroppedBox.x;

                    break;
                default:
                    break;
            }

            if (x >= this.state.cropBox.x)
                x = this.state.cropBox.x;
            if (y >= this.state.cropBox.y)
                y = this.state.cropBox.y;
            if ((x + this.state.unCroppedBox.width) <= (this.state.cropBox.x + this.state.cropBox.width))
                x = (this.state.cropBox.x + this.state.cropBox.width) - this.state.unCroppedBox.width;
            if ((y + this.state.unCroppedBox.height) <= (this.state.cropBox.y + this.state.cropBox.height))
                y = (this.state.cropBox.y + this.state.cropBox.height) - this.state.unCroppedBox.height;

            if (x >= this.state.cropBox.x + this.state.cropBox.width) {
                x = this.state.cropBox.x + this.state.cropBox.width;
            }
            if ((x + this.state.unCroppedBox.width) <= this.state.cropBox.x) {
                x = this.state.cropBox.x - this.state.unCroppedBox.width;
            }
            if (y >= this.state.cropBox.y + this.state.cropBox.height) {
                y = this.state.cropBox.y + this.state.cropBox.height;
            }
            if ((y + this.state.unCroppedBox.height) <= this.state.cropBox.y) {
                y = this.state.cropBox.y - this.state.unCroppedBox.height;
            }

            if (!this.moved)
                this.moved = true;
            this.unCroppedBoxElement.style.transform = "translate(" + x * this.props.zoomFactor + "px, " + y * this.props.zoomFactor + "px)";
            const flip = this.getFlipPosition();
            const angle = this.getAngle();

            if ((this.state.cropImage.x + (x - this.state.unCroppedBox.x)) < this.state.cropBox.x) {
                this.cropImageElem.style.transform = "translate(" + (this.state.cropImage.x + (x - this.state.unCroppedBox.x)) * this.props.zoomFactor + "px, " + (this.state.cropImage.y + (y - this.state.unCroppedBox.y)) * this.props.zoomFactor + "px) rotate(" + angle + "deg)" + flip;
            }

            let { unCroppedBox, cropImage } = this.state;
            unCroppedBox = { ...unCroppedBox };
            cropImage = { ...cropImage };

            if (this.moved) {
                cropImage = Object.assign(
                    cropImage, {
                    x: (cropImage.x + (x - unCroppedBox.x)),
                    y: (cropImage.y + (y - unCroppedBox.y))
                }
                );
                unCroppedBox = Object.assign(unCroppedBox, { x, y });
            }
            unCroppedBox.transforming = false;

            this.setState({ unCroppedBox, cropImage });
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.shortcutName !== prevProps.shortcutName) {
            this.handleKeyboardShortcuts(this.props.shortcutName)
        }
        
        //  Clear cached flip position when cropItem changes
        if (this.props.cropItem !== prevProps.cropItem) {
            this._cachedFlipPosition = null;
        }
    }
    
    componentWillUnmount() {
        //  Clean up animation frame on unmount to prevent memory leaks
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
        }
        
        // Remove any lingering event listeners
        window.removeEventListener('mousemove', this.moveSelection, false);
        window.removeEventListener('mouseup', this.stopMove, false);
        window.removeEventListener('touchmove', this.moveSelection, false);
        window.removeEventListener('touchend', this.stopMove, false);
        window.removeEventListener('mousemove', this.resizeCropSelection, false);
        window.removeEventListener('mouseup', this.stopCropResize, false);
        window.removeEventListener('touchmove', this.resizeCropSelection, false);
        window.removeEventListener('touchend', this.stopCropResize, false);
        window.removeEventListener('mousemove', this.resizeSelection, false);
        window.removeEventListener('mouseup', this.stopResize, false);
        window.removeEventListener('touchmove', this.resizeSelection, false);
        window.removeEventListener('touchend', this.stopResize, false);
    }

    getCropBoxPosition(e) {
        const mouse_position = this.getMousePosition(e);

        const dx = (mouse_position.x - this.mouseStartPosition.x) / this.props.zoomFactor;
        const dy = (mouse_position.y - this.mouseStartPosition.y) / this.props.zoomFactor;
        let cropX, cropY, cropWidth, cropHeight;

        if (this.state.cropBox.handle === "top-left") {
            cropX = this.state.cropBox.x + dx;
            cropY = this.state.cropBox.y + dy;
            cropWidth = this.state.cropBox.width - dx;
            cropHeight = this.state.cropBox.height - dy;
        }
        else if (this.state.cropBox.handle === "top-right") {
            cropX = this.state.cropBox.x;
            cropY = this.state.cropBox.y + dy;
            cropWidth = this.state.cropBox.width + dx;
            cropHeight = this.state.cropBox.height - dy;
        }
        else if (this.state.cropBox.handle === "bottom-right") {
            cropX = this.state.cropBox.x;
            cropY = this.state.cropBox.y;
            cropWidth = this.state.cropBox.width + dx;
            cropHeight = this.state.cropBox.height + dy;
        }
        else if (this.state.cropBox.handle === "bottom-left") {
            cropX = this.state.cropBox.x + dx;
            cropY = this.state.cropBox.y;
            cropWidth = this.state.cropBox.width - dx;
            cropHeight = this.state.cropBox.height + dy;
        }
        else if (this.state.cropBox.handle === "left") {
            cropX = this.state.cropBox.x + dx;
            cropWidth = this.state.cropBox.width - dx;
            cropY = this.state.cropBox.y;
            cropHeight = this.state.cropBox.height;
        }
        else if (this.state.cropBox.handle === "top") {
            cropX = this.state.cropBox.x;
            cropY = this.state.cropBox.y + dy;
            cropWidth = this.state.cropBox.width;
            cropHeight = this.state.cropBox.height - dy;
        }
        else if (this.state.cropBox.handle === "right") {
            cropX = this.state.cropBox.x;
            cropY = this.state.cropBox.y;
            cropWidth = this.state.cropBox.width + dx;
            cropHeight = this.state.cropBox.height;
        }
        else if (this.state.cropBox.handle === "bottom") {
            cropX = this.state.cropBox.x;
            cropY = this.state.cropBox.y;
            cropWidth = this.state.cropBox.width;
            cropHeight = this.state.cropBox.height + dy;
        }

        if (cropX <= this.state.unCroppedBox.x) {
            cropWidth -= this.state.unCroppedBox.x - cropX;
            cropX = this.state.unCroppedBox.x;
        }
        if (cropY <= this.state.unCroppedBox.y) {
            cropHeight -= this.state.unCroppedBox.y - cropY;
            cropY = this.state.unCroppedBox.y;
        }

        if (((cropX + cropWidth - this.state.unCroppedBox.width) >= this.state.unCroppedBox.x)) {
            cropWidth -= (cropX + cropWidth - this.state.unCroppedBox.width) - this.state.unCroppedBox.x;
        }

        if ((cropY + cropHeight - this.state.unCroppedBox.height) >= this.state.unCroppedBox.y) {
            cropHeight -= (cropY + cropHeight - this.state.unCroppedBox.height) - this.state.unCroppedBox.y;
        }

        const width = this.props.cropItem.get("width");
        const height = this.props.cropItem.get("height");

        if (cropWidth < 50) {
            if (!this.xLocked)
                this.lockedX = cropX;
            cropX -= cropX - this.lockedX;
            this.xLocked = true;
            cropWidth = width <= 50 ? Math.max(width / 2, cropWidth) : 50;
        } else if (this.xLocked) {
            this.xLocked = false;
        }

        if (cropHeight < 50) {
            if (!this.yLocked)
                this.lockedY = cropY;
            cropY -= cropY - this.lockedY;
            this.yLocked = true;
            cropHeight = height <= 50 ? Math.max(height / 2, cropHeight) : 50;
        } else if (this.yLocked) {
            this.yLocked = false;
        }

        return { x: cropX, y: cropY, width: cropWidth, height: cropHeight };
    }

    initiateCropResize(e) {
        try {
            e.preventDefault();
            e.stopPropagation();

            const handle = e.target.getAttribute("data-handle");
            const mouse_position = this.getMousePosition(e);
            this.mouseStartPosition = Object.assign(this.mouseStartPosition, { x: mouse_position.x, y: mouse_position.y });
            const cropBox = { ...this.state.cropBox };
            this.setState({ cropBox: Object.assign(cropBox, { "handle": handle }) });

            window.addEventListener('mousemove', this.resizeCropSelection, false);
            window.addEventListener('mouseup', this.stopCropResize, false);
            window.addEventListener('touchmove', this.resizeCropSelection, false);
            window.addEventListener('touchend', this.stopCropResize, false);
        } catch (error) { }
    }

    resizeCropSelection(e) {
        try {
            e.preventDefault();
            e.stopPropagation();
            
            // Use requestAnimationFrame for smooth resize updates
            if (this.animationFrameId) {
                cancelAnimationFrame(this.animationFrameId);
            }
            
            const crop = this.getCropBoxPosition(e);
            
            this.animationFrameId = requestAnimationFrame(() => {
                const { cropHandles, cropBoxElement, cropImageElem } = this;
                if (!cropBoxElement || !cropHandles || !cropImageElem) return;
                
                const { zoomFactor } = this.props;
                const cropX = (crop.x * zoomFactor).toFixed(2);
                const cropY = (crop.y * zoomFactor).toFixed(2);
                const cropW = `${crop.width * zoomFactor}px`;
                const cropH = `${crop.height * zoomFactor}px`;
                const translateStr = `translate(${cropX}px, ${cropY}px)`;
                
                // Batch style updates
                cropBoxElement.style.cssText = `transform: ${translateStr}; width: ${cropW}; height: ${cropH};`;
                cropHandles.style.cssText = `transform: ${translateStr}; width: ${cropW}; height: ${cropH};`;
                
                const flip = this.getFlipPosition();
                const angle = this.getAngle();
                const imgX = ((this.state.cropImage.x - (crop.x - this.state.cropBox.x)) * zoomFactor).toFixed(2);
                const imgY = ((this.state.cropImage.y - (crop.y - this.state.cropBox.y)) * zoomFactor).toFixed(2);
                cropImageElem.style.transform = `translate(${imgX}px, ${imgY}px) rotate(${angle}deg)${flip}`;
            });
        } catch (error) { }
    }

    stopCropResize(e) {
        try {
            e.preventDefault();
            e.stopPropagation();
            const crop = this.getCropBoxPosition(e);

            let { cropImage, cropBox } = this.state;
            cropImage = { ...cropImage };
            cropBox = { ...cropBox };

            cropImage = Object.assign(
                cropImage,
                { x: (this.state.cropImage.x - (crop.x - this.state.cropBox.x)), y: (this.state.cropImage.y - (crop.y - this.state.cropBox.y)) }
            );
            cropBox = Object.assign(cropBox, { x: crop.x, y: crop.y, width: crop.width, height: crop.height });

            this.setState({ cropImage, cropBox });
        } catch (error) { }
        window.removeEventListener('mousemove', this.resizeCropSelection, false);
        window.removeEventListener('mouseup', this.stopCropResize, false);
        window.removeEventListener('touchmove', this.resizeCropSelection, false);
        window.removeEventListener('touchend', this.stopCropResize, false);
    }

    getUnCroppedResizePosition(e) {
        const mouse_position = this.getMousePosition(e);

        const mouseX = this.state.unCroppedBox.x - (this.mouseStartPosition.x - mouse_position.x);
        const mouseY = this.state.unCroppedBox.x - (this.mouseStartPosition.y - mouse_position.y);

        let unCropX, unCropY, unCropWidth, unCropHeight;

        if (this.state.unCroppedBox.handle === "top-left") {
            unCropWidth = this.state.unCroppedBox.width - (mouseX - this.state.unCroppedBox.x);
            unCropHeight = this.state.unCroppedBox.height - (mouseY - this.state.unCroppedBox.y);
            unCropX = mouseX;
            unCropY = mouseY - ((unCropWidth / this.state.unCroppedBox.width * this.state.unCroppedBox.height) - unCropHeight);
        } else if (this.state.unCroppedBox.handle === "top-right") {
            unCropWidth = mouseX - this.state.unCroppedBox.x + this.state.unCroppedBox.width;
            unCropHeight = this.state.unCroppedBox.height - (mouseY - this.state.unCroppedBox.y);
            unCropX = this.state.unCroppedBox.x;
            unCropY = mouseY - ((unCropWidth / this.state.unCroppedBox.width * this.state.unCroppedBox.height) - unCropHeight);
        } else if (this.state.unCroppedBox.handle === "bottom-right") {
            unCropWidth = mouseX - this.state.unCroppedBox.x + this.state.unCroppedBox.width;
            unCropHeight = mouseY - this.state.unCroppedBox.y + this.state.unCroppedBox.height;
            unCropX = this.state.unCroppedBox.x;
            unCropY = this.state.unCroppedBox.y;
        } else {
            unCropWidth = this.state.unCroppedBox.width - (mouseX - this.state.unCroppedBox.x);
            unCropHeight = mouseY - this.state.unCroppedBox.y;
            unCropX = mouseX;
            unCropY = this.state.unCroppedBox.y;
        }

        unCropHeight = unCropWidth / this.state.unCroppedBox.width * this.state.unCroppedBox.height;

        let dW, dH;
        if (this.state.unCroppedBox.handle === "top-left") {
            if (unCropX >= this.state.cropBox.x) {
                dW = this.state.cropBox.x - unCropX;
                unCropWidth -= dW;
                unCropHeight -= dW / this.state.unCroppedBox.width * this.state.unCroppedBox.height;
                unCropX = this.state.cropBox.x;
                unCropY = this.props.workspaceBounds.get("cy") - (unCropHeight - this.state.unCroppedBox.dB) - (this.props.workspaceBounds.get("cy") - this.props.workspaceHeight / 2);
            }
            if (unCropY >= this.state.cropBox.y) {
                dH = this.state.cropBox.y - unCropY;
                unCropHeight -= dH;
                unCropY = this.state.cropBox.y;
                unCropWidth -= dH / this.state.unCroppedBox.height * this.state.unCroppedBox.width;
                unCropX = this.props.workspaceBounds.get("cx") - (unCropWidth - this.state.unCroppedBox.dR) - (this.props.workspaceBounds.get("cx") - this.props.workspaceWidth / 2);
            }
        }
        else if (this.state.unCroppedBox.handle === "top-right") {
            if (this.state.cropBox.x - unCropX + this.state.cropBox.width >= unCropWidth) {
                dW = unCropWidth - (this.state.cropBox.x - unCropX + this.state.cropBox.width);
                unCropWidth -= dW;
                unCropHeight -= dW / this.state.unCroppedBox.width * this.state.unCroppedBox.height;
                unCropY = this.props.workspaceBounds.get("cy") - (unCropHeight - this.state.unCroppedBox.dB) - (this.props.workspaceBounds.get("cy") - this.props.workspaceHeight / 2);
            }
            if (unCropY >= this.state.cropBox.y) {
                dH = this.state.cropBox.y - unCropY;
                unCropHeight -= dH;
                unCropWidth -= dH / this.state.unCroppedBox.height * this.state.unCroppedBox.width;
                unCropY = this.state.cropBox.y;
            }
        }
        else if (this.state.unCroppedBox.handle === "bottom-right") {

            if (this.state.cropBox.x - unCropX + this.state.cropBox.width >= unCropWidth) {
                dW = unCropWidth - (this.state.cropBox.x - unCropX + this.state.cropBox.width);
                unCropWidth -= dW;
                unCropHeight -= dW / this.state.unCroppedBox.width * this.state.unCroppedBox.height;
            }
            if (this.state.cropBox.y - unCropY + this.state.cropBox.height >= unCropHeight) {
                dH = unCropHeight - (this.state.cropBox.y - unCropY + this.state.cropBox.height);
                unCropHeight -= dH;
                unCropWidth -= dH / this.state.unCroppedBox.height * this.state.unCroppedBox.width;
            }
        } else {
            if (unCropX >= this.state.cropBox.x) {
                dW = this.state.cropBox.x - unCropX;
                unCropWidth -= this.state.cropBox.x - unCropX;
                unCropX = this.state.cropBox.x;
                unCropHeight -= dW / this.state.unCroppedBox.width * this.state.unCroppedBox.height;
            }
            if (this.state.cropBox.y - unCropY + this.state.cropBox.height >= unCropHeight) {
                dH = unCropHeight - (this.state.cropBox.y - unCropY + this.state.cropBox.height);
                unCropHeight -= dH;
                unCropWidth -= dH / this.state.unCroppedBox.height * this.state.unCroppedBox.width;
                unCropX = this.props.workspaceBounds.get("cx") - (unCropWidth - this.state.unCroppedBox.dR) - (this.props.workspaceBounds.get("cx") - this.props.workspaceWidth / 2);
            }
        }

        return { x: unCropX, y: unCropY, width: unCropWidth, height: unCropHeight };
    }

    initiateResize(e) {
        try {
            e.preventDefault();
            e.stopPropagation();
            const handle = e.target.getAttribute("data-handle");
            const mouse_position = this.getMousePosition(e);
            this.mouseStartPosition = Object.assign(this.mouseStartPosition, { x: mouse_position.x, y: mouse_position.y });

            let { unCroppedBox } = this.state;
            unCroppedBox = { ...unCroppedBox };
            unCroppedBox = Object.assign(unCroppedBox, { "handle": handle, transforming: true, dR: (((this.props.workspaceBounds.get("cx") - this.props.workspaceWidth / 2) + this.state.unCroppedBox.x + this.state.unCroppedBox.width) - this.props.workspaceBounds.get("cx")), dB: (((this.props.workspaceBounds.get("cy") - this.props.workspaceHeight / 2) + this.state.unCroppedBox.y + this.state.unCroppedBox.height) - this.props.workspaceBounds.get("cy")) });

            this.setState({ unCroppedBox });
        } catch (error) { }

        window.addEventListener('mousemove', this.resizeSelection, false);
        window.addEventListener('mouseup', this.stopResize, false);
        window.addEventListener('touchmove', this.resizeSelection, false);
        window.addEventListener('touchend', this.stopResize, false);
    }

    resizeSelection(e) {
        try {
            e.preventDefault();
            e.stopPropagation();
            
            // Use requestAnimationFrame for smooth resize updates
            if (this.animationFrameId) {
                cancelAnimationFrame(this.animationFrameId);
            }

            const unCrop = this.getUnCroppedResizePosition(e);
            
            this.animationFrameId = requestAnimationFrame(() => {
                const { unCroppedBoxElement, cropImageElem } = this;
                if (!unCroppedBoxElement || !cropImageElem) return;
                
                const { zoomFactor } = this.props;
                const unCropX = (unCrop.x * zoomFactor).toFixed(2);
                const unCropY = (unCrop.y * zoomFactor).toFixed(2);
                const unCropW = `${unCrop.width * zoomFactor}px`;
                const unCropH = `${unCrop.height * zoomFactor}px`;
                
                unCroppedBoxElement.style.transform = `translate(${unCropX}px, ${unCropY}px)`;
                unCroppedBoxElement.style.width = unCropW;
                unCroppedBoxElement.style.height = unCropH;
                
                const flip = this.getFlipPosition();
                const angle = this.getAngle();
                const imgX = (this.state.cropImage.x + (unCrop.x - this.state.unCroppedBox.x)) * zoomFactor;
                const imgY = (this.state.cropImage.y + (unCrop.y - this.state.unCroppedBox.y)) * zoomFactor;
                const imgW = `${(this.state.cropImage.width + (unCrop.width - this.state.unCroppedBox.width)) * zoomFactor}px`;
                const imgH = `${(this.state.cropImage.height + (unCrop.height - this.state.unCroppedBox.height)) * zoomFactor}px`;
                
                cropImageElem.style.transform = `translate(${imgX}px, ${imgY}px) rotate(${angle}deg)${flip}`;
                cropImageElem.style.width = imgW;
                cropImageElem.style.height = imgH;
            });
        } catch (error) { }
    }

    stopResize(e) {
        try {
            e.preventDefault();
            e.stopPropagation();
            const unCrop = this.getUnCroppedResizePosition(e);

            let { unCroppedBox, cropImage } = this.state;
            unCroppedBox = { ...unCroppedBox };
            cropImage = { ...cropImage };
            cropImage = Object.assign(
                cropImage,
                {
                    x: (this.state.cropImage.x + (unCrop.x - this.state.unCroppedBox.x)),
                    y: (this.state.cropImage.y + (unCrop.y - this.state.unCroppedBox.y)),
                    width: (this.state.cropImage.width + (unCrop.width - this.state.unCroppedBox.width)),
                    height: (this.state.cropImage.height + (unCrop.height - this.state.unCroppedBox.height))
                }
            );
            unCroppedBox = Object.assign(unCroppedBox, { x: unCrop.x, y: unCrop.y, width: unCrop.width, height: unCrop.height, transforming: false });

            this.setState({ unCroppedBox, cropImage });
        } catch (error) { }

        window.removeEventListener('mousemove', this.resizeSelection, false);
        window.removeEventListener('mouseup', this.stopResize, false);
        window.removeEventListener('touchmove', this.resizeSelection, false);
        window.removeEventListener('touchend', this.stopResize, false);
    }

    handleCropClick() {
        try {
            // Restore the static (non-panned) positions before saving:
            // - unCroppedBox.x was NOT shifted, so oX is clean
            // - cropBox.x was shifted by -panOffset, so add panOffset back to get the original position
            const panOffset = this.aiReframePanOffset || 0;
            const oX = this.state.cropSelectionBox.x + this.state.unCroppedBox.x;
            const oY = this.state.cropSelectionBox.y + this.state.unCroppedBox.y;
            const oWidth = this.state.unCroppedBox.width;
            const oHeight = this.state.unCroppedBox.height;

            const cX = (this.state.cropSelectionBox.x + this.state.cropBox.x) + panOffset;
            const cY = (this.state.cropSelectionBox.y + this.state.cropBox.y);
            const cWidth = this.state.cropBox.width;
            const cHeight = this.state.cropBox.height;
            const { cropItem } = this.props;

            let xDiff = ((oWidth - (cWidth + cX - oX))) - (oWidth - cWidth), yDiff = ((oHeight - (cHeight + cY - oY))) - (oHeight - cHeight);

            if (cropItem.get("flipPosition") === 1) {
                xDiff = 0 - ((oWidth - (cWidth + cX - oX)));
            } else if (cropItem.get("flipPosition") === 2) {
                yDiff = 0 - ((oHeight - (cHeight + cY - oY)));
            } else if (cropItem.get("flipPosition") === 3) {
                xDiff = 0 - ((oWidth - (cWidth + cX - oX)));
                yDiff = 0 - ((oHeight - (cHeight + cY - oY)));
            }

            const cropData = {
                x: cX,
                y: cY,
                width: cWidth,
                height: cHeight,
                original: { x: xDiff / cWidth, y: yDiff / cHeight, width: oWidth / cWidth, height: oHeight / cHeight },
                isCropped: true,
            };

            const isSingleClipFrame = cropItem.get("isSingleClipFrame");
            if (isSingleClipFrame) {
                const firstClipId = cropItem.get("clipDetails")?.keySeq().first();
                if (firstClipId) {
                    this.props.applyFrameClipPlot({
                        container: "workspaceItems",
                        id: cropItem.get("id"),
                        clipId: firstClipId,
                        original: cropData.original,
                    });
                    this.props.toggleCrop();
                }
            } else {
                this.props.cropImage({
                    container: "workspaceItems",
                    selectedItem: this.props.cropItem.get("id"),
                    toUpdate: cropData,
                });
            }
        } catch (error) { }
    }

    cancelCrop() {
        try {
            this.props.toggleCrop();
        } catch (error) { }
    }

    // : Memoized angle getter
    getAngle() {
        return 0;
    }

    // : Memoized flip position to avoid repeated calculations during drag
    getFlipPosition() {
        if (this._cachedFlipPosition !== null) {
            return this._cachedFlipPosition;
        }
        
        if (this.props.cropItem && this.props.cropItem.get("flipPosition")) {
            const flipValue = this.props.cropItem.get("flipPosition");
            this._cachedFlipPosition = (flipValue === 1) ? " scaleX(-1)" : (flipValue === 2) ? " scaleY(-1)" : (flipValue === 3) ? " scaleX(-1) scaleY(-1)" : "";
        } else {
            this._cachedFlipPosition = "";
        }
        return this._cachedFlipPosition;
    }

    resetCrop() {
        try {
            const stateToUpdate = {};

            stateToUpdate.cropSelectionBox = { x: this.props.cropItem.get("x"), y: this.props.cropItem.get("y"), width: this.props.cropItem.get("width"), height: this.props.cropItem.get("height"), angle: this.props.cropItem.get("angle") };
            stateToUpdate.cropBox = { x: 0, y: 0, width: this.state.cropSelectionBox.width, height: this.state.cropSelectionBox.height, angle: this.props.cropItem.get("angle") };
            stateToUpdate.cropImage = { x: 0, y: 0, width: this.state.cropSelectionBox.width, height: this.state.cropSelectionBox.height, angle: this.props.cropItem.get("angle") };
            stateToUpdate.unCroppedBox = { x: 0, y: 0, width: this.state.cropSelectionBox.width, height: this.state.cropSelectionBox.height, angle: this.props.cropItem.get("angle") };

            this.setState(stateToUpdate);
        } catch (error) { }
    }

    // : Extracted style computation helper to reduce render complexity
    computeStyles() {
        const { zoomFactor } = this.props;
        const { cropSelectionBox, cropBox, unCroppedBox, cropImage, unCroppedImagePanOffset = 0 } = this.state;
        const flip = this.getFlipPosition();
        const angle = this.getAngle();
        
        const toFixed2 = (val) => (val * zoomFactor).toFixed(2);
        const toPx = (val) => `${toFixed2(val)}px`;
        
        return {
            selectionStyles: cropSelectionBox ? {
                transform: `translate(${toFixed2(cropSelectionBox.x)}px, ${toFixed2(cropSelectionBox.y)}px) rotateZ(${cropSelectionBox.angle}deg)`,
                width: toPx(cropSelectionBox.width),
                height: toPx(cropSelectionBox.height)
            } : {},
            cropStyles: {
                transform: `translate(${toFixed2(cropBox.x)}px, ${toFixed2(cropBox.y)}px)`,
                width: toPx(cropBox.width),
                height: toPx(cropBox.height)
            },
            unCroppedStyle: {
                transform: `translate(${toFixed2(unCroppedBox.x)}px, ${toFixed2(unCroppedBox.y)}px)`,
                width: toPx(unCroppedBox.width),
                height: toPx(unCroppedBox.height)
            },
            cropImageStyle: {
                transform: `translate(${toFixed2(cropImage.x)}px, ${toFixed2(cropImage.y)}px) rotate(${angle}deg)${flip}`,
                width: toPx(unCroppedBox.width),
                height: toPx(unCroppedBox.height)
            },
            unCroppedImageStyle: {
                transform: (unCroppedImagePanOffset !== 0
                    ? `translateX(${unCroppedImagePanOffset * zoomFactor}px) `
                    : "") + `rotate(${angle}deg)${flip}`,
            },
            flip,
            angle
        };
    }

    render() {
        // : Compute all styles in one pass
        const { selectionStyles, cropStyles, unCroppedStyle, cropImageStyle, unCroppedImageStyle } = this.computeStyles();
        const filterSVGId = `scene-item-filtersvg-${this.props.cropItem.get("id")}`;
        const filterId = `${filterSVGId}-filter`;

        // ─── Avatar frame shape clip path ────────────────────────────────────
        // When the crop item is an avatar wrapped in a single-clip frame (e.g. circle),
        // apply the frame's SVG clip path to the crop box and handles so they
        // visually conform to the avatar's background shape.
        const isSingleClipFrame = this.props.cropItem.get("isSingleClipFrame");
        let avatarClipPathId = null;
        let avatarClipSVG = null;
        let cropBoxClipStyle = {};
        let cropHandlesClipStyle = {};
        let avatarShapeOverlay = null;

        if (isSingleClipFrame) {
            const firstClipId = this.props.cropItem.get("clipDetails")?.keySeq().first();
            const rawClipData = firstClipId
                ? this.props.cropItem.getIn(["clipDetails", firstClipId, "clipData"])
                : null;
            const defaultWidth = this.props.cropItem.get("defaultWidth") || this.props.cropItem.get("width");
            const defaultHeight = this.props.cropItem.get("defaultHeight") || this.props.cropItem.get("height");

            if (rawClipData && defaultWidth && defaultHeight) {
                const cropBoxW = parseFloat(cropStyles.width);
                const cropBoxH = parseFloat(cropStyles.height);
                const scaleX = cropBoxW / defaultWidth;
                const scaleY = cropBoxH / defaultHeight;

                avatarClipPathId = `avatar-crop-clip-${this.props.cropItem.get("id")}`;
                avatarClipSVG = (
                    <svg
                        width="0"
                        height="0"
                        style={{ position: "absolute", overflow: "hidden" }}
                    >
                        <defs>
                            <clipPath id={avatarClipPathId} clipPathUnits="userSpaceOnUse">
                                <path d={rawClipData} transform={`scale(${scaleX}, ${scaleY})`} />
                            </clipPath>
                        </defs>
                    </svg>
                );
                cropBoxClipStyle = { clipPath: `url(#${avatarClipPathId})` };
                cropHandlesClipStyle = { clipPath: `url(#${avatarClipPathId})` };

                const ASPECT_RATIO = 1.1666666667;
                const strokeWidth = (2 / ASPECT_RATIO) * (defaultWidth / cropBoxW);
                avatarShapeOverlay = (
                    <div
                        style={{
                            width: `${defaultWidth}px`,
                            height: `${defaultHeight}px`,
                            transformOrigin: "0 0",
                            transform: `scaleX(${scaleX}) scaleY(${scaleY})`,
                            position: "absolute",
                            top: 0,
                            left: 0,
                            pointerEvents: "none",
                        }}
                    >
                        <svg width="100%" height="100%" style={{ position: "absolute", width: "100%", height: "100%" }}>
                            <path
                                d={rawClipData}
                                strokeWidth={strokeWidth}
                                stroke="#2CB9FF"
                                fill="none"
                            />
                        </svg>
                    </div>
                );
            }
        }
        // ─────────────────────────────────────────────────────────────────────


        const cropImgElem = (
            <div className="crop-imgHolder">
                <img
                    alt="cropImageElem"
                    ref={(instance) => { this.cropImageElem = instance }}
                    src={this.state.image}
                    style={{ ...cropImageStyle, filter: `url(#${filterId})` }}
                />
            </div>
        );
        const unCropImgElem = (
            <div className="crop-imgHolder">
                <img
                    alt="unCroppedImageElem"
                    src={this.state.image}
                    style={{ ...unCroppedImageStyle, filter: `url(#${filterId})` }}
                />
            </div>
        );

        let selectionBoxClass = "cropSelectionBox";
        if (this.state.cropSelectionBox.angle >= 337.5 || this.state.cropSelectionBox.angle <= 22.5)
            selectionBoxClass += " e"
        else if (this.state.cropSelectionBox.angle >= 22.5 && this.state.cropSelectionBox.angle <= 67.5)
            selectionBoxClass += " se"
        else if (this.state.cropSelectionBox.angle >= 67.5 && this.state.cropSelectionBox.angle <= 112.5)
            selectionBoxClass += " s"
        else if (this.state.cropSelectionBox.angle >= 112.5 && this.state.cropSelectionBox.angle <= 157.5)
            selectionBoxClass += " sw"
        else if (this.state.cropSelectionBox.angle >= 157.5 && this.state.cropSelectionBox.angle <= 202.5)
            selectionBoxClass += " w"
        else if (this.state.cropSelectionBox.angle >= 202.5 && this.state.cropSelectionBox.angle <= 247.5)
            selectionBoxClass += " nw"
        else if (this.state.cropSelectionBox.angle >= 247.5 && this.state.cropSelectionBox.angle <= 292.5)
            selectionBoxClass += " n"
        else if (this.state.cropSelectionBox.angle >= 292.5 && this.state.cropSelectionBox.angle <= 337.5)
            selectionBoxClass += " ne"

        const cropSceneStyle = {
            left: 0,
            top: 0,
            position: "absolute",
            pointerEvents: "none",
            width: (this.props.workspaceWidth * this.props.zoomFactor).toFixed(2) + "px",
            height: (this.props.workspaceHeight * this.props.zoomFactor).toFixed(2) + "px",
        };

        return (
            <>
                {avatarClipSVG}
                <div className="crop-scene crop-tool-bar" style={cropSceneStyle}>
                    <CropSelectionBox className={selectionBoxClass} style={selectionStyles} data-html2canvas-ignore="true">
                        <div
                            className="unCroppedBox"
                            ref={(instance) => { this.unCroppedBoxElement = instance }}
                            style={unCroppedStyle}
                            onTouchStart={this.initiateMove}
                            onMouseDown={this.initiateMove}
                        >
                            {unCropImgElem}
                            <div className="uncrop-box-handles">
                                <span className="resize-handle top-left resize-handle-cursor" data-handle="top-left" onTouchStart={this.initiateResize} onMouseDown={this.initiateResize} />
                                <span className="resize-handle top-right resize-handle-cursor" data-handle="top-right" onTouchStart={this.initiateResize} onMouseDown={this.initiateResize} />
                                <span className="resize-handle bottom-right resize-handle-cursor" data-handle="bottom-right" onTouchStart={this.initiateResize} onMouseDown={this.initiateResize} />
                                <span className="resize-handle bottom-left resize-handle-cursor" data-handle="bottom-left" onTouchStart={this.initiateResize} onMouseDown={this.initiateResize} />
                            </div>
                        </div>
                        <div
                            className="cropBox"
                            ref={(instance) => { this.cropBoxElement = instance }}
                            style={{ ...cropStyles, ...cropBoxClipStyle }}
                            onTouchStart={this.initiateMove}
                            onMouseDown={this.initiateMove}
                        >
                            {cropImgElem}
                        </div>
                        <div ref={(instance) => { this.cropHandles = instance }} className="crop-handles" style={{ ...cropStyles, ...cropHandlesClipStyle }}>
                            <img src={`${STATIC_PATH}transform-icons/crop-tl.svg`} alt="crop top left" className="crop-handle top-left resize-handle-cursor" data-handle="top-left" onMouseDown={this.initiateCropResize} onTouchStart={this.initiateCropResize} />
                            <img src={`${STATIC_PATH}transform-icons/crop-t.svg`} alt="crop top" className="crop-handle top resize-handle-cursor" data-handle="top" onMouseDown={this.initiateCropResize} onTouchStart={this.initiateCropResize} />
                            <img src={`${STATIC_PATH}transform-icons/crop-tl.svg`} alt="crop top right" className="crop-handle top-right resize-handle-cursor" data-handle="top-right" onMouseDown={this.initiateCropResize} onTouchStart={this.initiateCropResize} />
                            <img src={`${STATIC_PATH}transform-icons/crop-t.svg`} alt="crop left" className="crop-handle left resize-handle-cursor" data-handle="left" onMouseDown={this.initiateCropResize} onTouchStart={this.initiateCropResize} />
                            <img src={`${STATIC_PATH}transform-icons/crop-tl.svg`} alt="crop bottom left" className="crop-handle bottom-left resize-handle-cursor" data-handle="bottom-left" onMouseDown={this.initiateCropResize} onTouchStart={this.initiateCropResize} />
                            <img src={`${STATIC_PATH}transform-icons/crop-t.svg`} alt="crop bottom" className="crop-handle bottom resize-handle-cursor" data-handle="bottom" onMouseDown={this.initiateCropResize} onTouchStart={this.initiateCropResize} />
                            <img src={`${STATIC_PATH}transform-icons/crop-tl.svg`} alt="crop bottom right" className="crop-handle bottom-right resize-handle-cursor" data-handle="bottom-right" onMouseDown={this.initiateCropResize} onTouchStart={this.initiateCropResize} />
                            <img src={`${STATIC_PATH}transform-icons/crop-t.svg`} alt="crop right" className="crop-handle right resize-handle-cursor" data-handle="right" onMouseDown={this.initiateCropResize} onTouchStart={this.initiateCropResize} />
                        </div>
                        {avatarShapeOverlay && (
                            <div style={{ ...cropStyles, position: "absolute", pointerEvents: "none" }}>
                                {avatarShapeOverlay}
                            </div>
                        )}
                    </CropSelectionBox>
                </div>
                <ItemToolConfirmation
                    workspaceStage={this.props.workspaceStage}
                    onApply={this.handleCropClick}
                    onCancel={this.cancelCrop}
                />
            </>
        );
    }
}

CropComponent.propTypes = {
    workspaceStage: PropTypes.object,
    workspaceWidth: PropTypes.number,
    workspaceHeight: PropTypes.number,
    cropItem: PropTypes.object,
    workspaceRef: PropTypes.object,
    zoomFactor: PropTypes.number,
    shortcutName: PropTypes.string,
    workspaceBounds: PropTypes.object,
    cropImage: PropTypes.func,
    toggleCrop: PropTypes.func,
    applyFrameClipPlot: PropTypes.func,
    playhead: PropTypes.number,
    aiReframeX: PropTypes.number,
};

const mapStateToProps = (state) => {
    const cropItem = state.projectDetails.getIn(["workspaceItems", state.app.getIn(["selectedItems", 0])]);
    const zoomFactor = state.app.get("zoomFactor");
    const playhead = state.app.get("playhead");
    const projectWidth = state.projectDetails.get("width");
    const reframeData = state.projectDetails.get("reframeData");
    const appReframeData = state.app.get("reframeData");

    const aiReframeX = (cropItem && cropItem.get("sceneData"))
        ? getAiReframeStaticX(cropItem, playhead, reframeData, appReframeData, zoomFactor, projectWidth)
        : null;

    return {
        workspaceStage: state.app.get("workspaceStage"),
        workspaceWidth: projectWidth,
        workspaceHeight: state.projectDetails.get("height"),
        cropItem,
        zoomFactor,
        workspaceBounds: state.app.get("workspaceBounds"),
        playhead,
        aiReframeX,
    };
}

const mapDispatchToProps = (dispatch) => ({
    cropImage: (data) => dispatch(cropImage(data)),
    toggleCrop: () => dispatch(toggleCrop()),
    applyFrameClipPlot: (data) => dispatch(applyFrameClipPlot(data)),
});

const Crop = connect(mapStateToProps, mapDispatchToProps)(CropComponent);

export default Crop;
